/**
 * CustomerNotesPanel — read-only, expandable "Customer Notes" panel shown
 * inside the Proposal builder and Work Order view (iter241). Always live
 * from the customer + site records (no stale copies). Renders nothing when
 * the customer has no notes.
 */
import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, StickyNote } from "lucide-react";
import { format } from "date-fns";
import { getClient, getSites } from "@/lib/api";

export function CustomerNotesPanel({ clientId }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!clientId) { setNotes([]); return; }
    let alive = true;
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          getClient(clientId),
          getSites({ client_id: clientId }).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const merged = [];
        const seen = new Set();
        const push = (arr) => (arr || []).forEach(n => {
          const key = `${n.date}|${n.text}`;
          if (n.text && !seen.has(key)) { seen.add(key); merged.push(n); }
        });
        push(cRes.data?.notes);
        (sRes.data || []).forEach(s => push(s.notes));
        merged.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setNotes(merged);
      } catch { if (alive) setNotes([]); }
    })();
    return () => { alive = false; };
  }, [clientId]);

  if (!clientId || notes.length === 0) return null;
  const visible = showAll ? notes : notes.slice(0, 5);

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg" data-testid="customer-notes-panel">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        onClick={() => setOpen(o => !o)}
        data-testid="customer-notes-toggle"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-amber-500" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-500" />}
        <StickyNote className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-xs font-semibold text-amber-800">Customer Notes</span>
        <span className="text-[10px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{notes.length}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {visible.map((n, i) => (
            // eslint-disable-next-line react/no-array-index-key -- read-only merged log
            <div key={`cn-${i}`} className="text-xs bg-white border border-amber-100 rounded px-2 py-1.5" data-testid={`customer-note-${i}`}>
              <span className="text-amber-700 font-semibold text-[10px]">
                {n.date ? format(new Date(n.date + "T00:00:00"), "MMM d, yyyy") : ""}
              </span>
              <span className="ml-1.5 text-slate-700">{n.text}</span>
            </div>
          ))}
          {notes.length > 5 && (
            <button
              type="button"
              className="text-[11px] text-amber-700 font-medium hover:underline"
              onClick={() => setShowAll(s => !s)}
              data-testid="customer-notes-show-all"
            >
              {showAll ? "Show less" : `Show all ${notes.length} notes`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerNotesPanel;
